"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  fetchUsageSummary,
  listMyJobs,
  type JobSummary,
  type UsageSummary,
} from "../../lib/api";
import type { Viewport } from "../../lib/audit-types";
import { planLabel } from "../../lib/auth";
import { useAppAuth } from "../app-shell";
import { AppToMarketingLink } from "../components/app-to-marketing-link";

/* ------------------------------------------------------------------ */
/*  Types                                                               */
/* ------------------------------------------------------------------ */

type JobsState =
  | { status: "loading" }
  | { status: "ready"; jobs: JobSummary[] }
  | { status: "error"; message: string };

// PLAN_BLURB was dropped along with the old bottom "Your plan" card
// that lived under the recent-runs table. The credit-cost rules now
// live in the aside as a structured list, not a per-plan sentence.

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export function UsageClient() {
  const auth = useAppAuth();
  const [jobs, setJobs] = useState<JobsState>({ status: "loading" });
  // Authoritative usage stats from the credit ledger — survive audit
  // deletion. Null until loaded or if the endpoint is unavailable, in
  // which case we fall back to deriving stats from the (deletable) jobs.
  const [summary, setSummary] = useState<UsageSummary | null>(null);

  const token = auth.status === "authenticated" ? auth.token : null;
  const entitlements = auth.status === "authenticated" ? auth.entitlements : null;
  const plan = entitlements?.plan ?? "free";

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // Recent-runs list still comes from the jobs list (clicking a row opens
    // the audit — a deleted audit shouldn't appear there).
    listMyJobs(token, 60)
      .then((list) => { if (!cancelled) setJobs({ status: "ready", jobs: list }); })
      .catch((err) => {
        if (!cancelled) {
          const message = err instanceof ApiError || err instanceof Error ? err.message : "Could not load recent usage.";
          setJobs({ status: "error", message });
        }
      });
    // Headline stats + trend come from the persistent ledger summary.
    fetchUsageSummary(token)
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, [token]);

  const allJobs = useMemo(() => (jobs.status === "ready" ? jobs.jobs : []), [jobs]);
  const monthlyJobs = useMemo(() => allJobs.filter((j) => j.status === "done" && isThisMonth(j.createdAt)), [allJobs]);
  const lastMonthJobs = useMemo(() => allJobs.filter((j) => j.status === "done" && isLastMonth(j.createdAt)), [allJobs]);

  // Prefer the ledger summary; fall back to job-derived stats only when the
  // endpoint is unavailable (e.g. backend not yet deployed).
  const monthly = useMemo(
    () => summary?.monthly ?? summarize(monthlyJobs),
    [summary, monthlyJobs],
  );
  const lastMonth = useMemo(
    () => summary?.lastMonth ?? summarize(lastMonthJobs),
    [summary, lastMonthJobs],
  );

  const recentRuns = useMemo(
    () => [...allJobs].sort((a, b) => b.createdAt - a.createdAt).slice(0, 6),
    [allJobs],
  );

  // 30-day trend buckets (credits per day)
  const trend = useMemo(
    () => summary?.trend ?? buildTrend(allJobs, 30),
    [summary, allJobs],
  );

  if (auth.status !== "authenticated" || !entitlements) return <UsageSkeleton />;

  const credits = entitlements.credits.balance;
  const monthlyGrant = entitlements.credits.monthlyGrant;
  const usedPct = monthlyGrant > 0
    ? Math.min(100, Math.round((monthly.credits / monthlyGrant) * 100))
    : 0;
  // Authoritative per-bucket balances when present; fall back to inferring
  // from total vs grant for API responses predating the split.
  const monthlyLeft =
    typeof entitlements.credits.monthly === "number"
      ? entitlements.credits.monthly
      : Math.max(0, monthlyGrant - monthly.credits);
  const topUp =
    typeof entitlements.credits.topup === "number"
      ? entitlements.credits.topup
      : Math.max(0, credits - Math.max(0, monthlyGrant - monthly.credits));
  const daysUntilRenewal = daysToRenewal();
  const renewsOn = formatRenewalDate();
  // Free-plan users don't have a "renewal" — they have a monthly
  // credit refresh. Using "Renews" for them implied a subscription
  // they don't have. Verb adapts to the plan; cadence + date stay the
  // same so the chrome is consistent.
  const renewVerb = plan === "free" ? "Credits refresh" : "Renews";
  const avgPerRun = monthly.runs > 0 ? Math.round((monthly.credits / monthly.runs) * 10) / 10 : 0;
  const deltaPct = lastMonth.credits > 0
    ? Math.round(((monthly.credits - lastMonth.credits) / lastMonth.credits) * 100)
    : null;
  const projection = projectMonthlyTotal(monthly.credits, daysUntilRenewal);
  const isLow = monthlyGrant > 0 && credits / monthlyGrant < 0.2;

  return (
    <main className="usg-page">
      {/* ── Header (full width above the 2-col grid) ── */}
      <header className="usg-page-header">
        <div>
          <h1 className="usg-h1">Usage</h1>
          <p className="usg-sub">
            <span className={`usg-plan-chip usg-plan-${plan}`}>{planLabel(plan)}</span>
            <span className="usg-sub-meta">
              {renewVerb} in {daysUntilRenewal} day{daysUntilRenewal !== 1 ? "s" : ""}
              <span className="usg-sub-sep" aria-hidden="true">·</span>
              {renewsOn}
            </span>
          </p>
        </div>
        <div className="usg-actions-top">
          {/* Manage plan deep-links to the plan section; Add credits
              deep-links to the top-up section. Previously both landed
              the user at the top of /account with the relevant
              section several scrolls down. Label swaps to "Upgrade"
              for free users — they can't manage anything from the
              plan section, only upgrade, so "Manage plan" misled
              about what they'd find. */}
          <Link href="/account#plan" className="usg-btn usg-btn-ghost">
            {plan === "free" ? "Upgrade plan" : "Manage plan"}
          </Link>
          <Link href="/account#topup" className="usg-btn usg-btn-fill">Add credits</Link>
        </div>
      </header>

      {/* ── 2-col body. Mirrors Dashboard so wide monitors render the
              same constrained reading column instead of letting tables
              and trend charts stretch full-bleed to 1440px. ── */}
      <div className="app-cols">
        <div className="app-main">
          {/* ── Stat grid ── */}
          <section className="usg-stats" aria-label="Usage summary">
            <article className={`usg-stat usg-stat-feature${isLow ? " is-low" : ""}`}>
              <span className="usg-stat-label">Credits left</span>
              <span className="usg-stat-value">{credits}</span>
              <div
                className="usg-progress"
                role="progressbar"
                aria-valuenow={usedPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${usedPct}% of monthly grant used`}
              >
                <span className="usg-progress-fill" style={{ width: `${usedPct}%` }} />
              </div>
              <span className="usg-stat-meta">
                {monthlyLeft} of {monthlyGrant} monthly{topUp > 0 ? ` · +${topUp} top-up` : ""}
              </span>
            </article>

            <article className="usg-stat">
              <span className="usg-stat-label">Used this month</span>
              <span className="usg-stat-value">{monthly.credits}</span>
              <span className="usg-stat-meta">
                {deltaPct === null ? (
                  /* Was "First month tracking" — but the trigger is
                     just "lastMonth.credits === 0", which fires for
                     anyone who didn't run audits last month, not only
                     brand-new users. Honest copy. */
                  <span className="usg-delta-neutral">No usage last month</span>
                ) : deltaPct > 0 ? (
                  <span className="usg-delta-up">↑ {deltaPct}% vs last month</span>
                ) : deltaPct < 0 ? (
                  <span className="usg-delta-down">↓ {Math.abs(deltaPct)}% vs last month</span>
                ) : (
                  <span className="usg-delta-neutral">Same as last month</span>
                )}
              </span>
            </article>

            <article className="usg-stat">
              <span className="usg-stat-label">Runs</span>
              <span className="usg-stat-value">{monthly.runs}</span>
              <span className="usg-stat-meta">
                {monthly.runs > 0 ? `${avgPerRun} credits / run avg` : "No runs this month"}
              </span>
            </article>

            <article className="usg-stat">
              <span className="usg-stat-label">Pages reviewed</span>
              <span className="usg-stat-value">{monthly.pages}</span>
              <span className="usg-stat-meta">
                across {monthly.runs} run{monthly.runs !== 1 ? "s" : ""}
              </span>
            </article>
          </section>

          {/* ── Trend (chart-first, projection as caption) ── */}
          <section className="usg-section">
            <header className="usg-sec-head">
              <h2 className="usg-sec-title">Last 30 days</h2>
            </header>
            <div className="usg-trend">
              {/* Chart leads, axis pins the dates, projection note
                  reads as the takeaway below. Previously the order
                  was note → chart → axis, which buried the visual
                  evidence under the conclusion. */}
              <Sparkline points={trend} />
              <div className="usg-trend-axis">
                <span>{formatShortDate(Date.now() - 29 * 24 * 60 * 60 * 1000)}</span>
                <span>{formatShortDate(Date.now() - 15 * 24 * 60 * 60 * 1000)}</span>
                <span>Today</span>
              </div>
              {(() => {
                // Only surface a note when there's something the user
                // can actually act on — i.e. their projected remaining
                // usage for the month exceeds their actual available
                // balance. In every other case (on-track, no data,
                // single run), the chart speaks for itself and the
                // note would just be noise.
                //
                // Why compare against `credits` (the live balance),
                // not `monthlyGrant`: monthlyGrant is only the
                // recurring allowance. A user can be sitting on a big
                // top-up balance and still be on pace to use more than
                // monthlyGrant this month — they're not "running out"
                // unless that pace would burn through their actual
                // balance.
                if (projection === null || monthly.runs < 2) return null;
                const projectedRemaining = projection - monthly.credits;
                const shortBy = projectedRemaining - credits;
                if (shortBy <= 0) return null;
                const renewWord = plan === "free" ? "refresh" : "renewal";
                return (
                  <div className="usg-trend-note" role="note">
                    <span className="usg-trend-note-icon" aria-hidden="true">
                      <svg
                        viewBox="0 0 20 20"
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M10 3 18 17H2L10 3Z" />
                        <path d="M10 9v4" />
                        <circle cx="10" cy="15.4" r="0.6" fill="currentColor" stroke="none" />
                      </svg>
                    </span>
                    <span className="usg-trend-note-text">
                      <strong>
                        You&rsquo;re projected to run out of credits before {renewWord}.
                      </strong>{" "}
                      At your current pace, you&rsquo;ll need about ~{projectedRemaining} more
                      credits this month — roughly {shortBy} more than your current balance of{" "}
                      {credits}. Top up to keep going, or upgrade for more headroom.
                    </span>
                  </div>
                );
              })()}
            </div>
          </section>

          {/* ── Recent runs (Status promoted, View all → /projects) ── */}
          <section className="usg-section">
            <header className="usg-sec-head">
              <h2 className="usg-sec-title">Recent runs</h2>
              {/* Was /dashboard — but Dashboard only shows a curated
                  few, the actual library is /projects. */}
              <Link href="/projects" className="usg-sec-link">View all →</Link>
            </header>
            {jobs.status === "loading" ? (
              <p className="usg-empty">Loading recent runs…</p>
            ) : jobs.status === "error" ? (
              <p className="usg-empty">{jobs.message}</p>
            ) : recentRuns.length === 0 ? (
              <p className="usg-empty">No runs yet. Start an audit to see history here.</p>
            ) : (
              <>
              <div className="usg-table-wrap">
                <table className="usg-table">
                  <thead>
                    <tr>
                      <th>Site</th>
                      {/* Status promoted from last column to second —
                          it's the most scannable signal of "what
                          happened with this run" and was buried at the
                          far right where the eye lands last. */}
                      <th>Status</th>
                      <th>Viewports</th>
                      <th className="ralign">Pages</th>
                      <th className="ralign">Credits</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map((job) => (
                      <tr key={job.id}>
                        <td>
                          <Link href={`/audit/${job.id}`} className="usg-row-link">
                            {job.siteName || hostnameFor(job.sourceUrl)}
                          </Link>
                        </td>
                        <td>
                          <span className={`usg-status usg-status-${job.status}`}>
                            {statusLabel(job.status)}
                          </span>
                        </td>
                        <td>
                          {(job.viewports || []).map((v) => (
                            <span key={v} className="usg-vp">{viewportLabel(v)}</span>
                          ))}
                        </td>
                        <td className="ralign">{job.pageCount || 0}</td>
                        <td className="ralign usg-cell-credits">{estimateBaseCredits(job)}</td>
                        <td className="usg-cell-time">{relativeTime(job.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {recentRuns.length < 6 && (
                <p className="usg-table-hint">Older runs will appear here as you go.</p>
              )}
              </>
            )}
          </section>
        </div>

        {/* ── Aside: credit-cost rules ──
            The old bottom "Your plan" card duplicated the plan chip
            from the header and the Manage/Add credits buttons from
            the header's action group. The only unique content was the
            credit-cost rules, which moved into the aside formatted as
            a scannable list (4 rules jammed in one sentence with
            center dots was unreadable). The "top-ups never expire"
            line was also wrong — Account says 12 months; aligning to
            that single source of truth. */}
        <aside className="app-aside">
          <article className="usg-rules">
            <h2 className="usg-rules-title">How credits work</h2>
            <dl className="usg-rules-list">
              <div className="usg-rule">
                <dt>1 credit</dt>
                <dd>per page, per viewport.</dd>
              </div>
              <div className="usg-rule">
                <dt>Desktop + Mobile</dt>
                <dd>doubles the page cost.</dd>
              </div>
              <div className="usg-rule">
                <dt>AI assistance</dt>
                <dd>adds 5 credits per run.</dd>
              </div>
              <div className="usg-rule">
                <dt>Top-ups</dt>
                <dd>stay valid for 12 months.</dd>
              </div>
            </dl>
            {/* No trailing → arrow here — AppToMarketingLink already
                appends an external-link glyph because the link
                crosses from the tool into marketing. Two icons for
                the same "this opens elsewhere" job was visual noise. */}
            <AppToMarketingLink href="/pricing#plans" className="usg-rules-link">
              Compare plans
            </AppToMarketingLink>
          </article>

          {/* Credit history — a light trail of the ledger so "why is my
              balance N?" is self-answerable: grants, charges, refunds and
              top-ups, newest first. Deliberately not a dense table. */}
          {summary?.creditEvents && summary.creditEvents.length > 0 ? (
            <article className="usg-rules">
              <h2 className="usg-rules-title">Credit history</h2>
              <ul className="usg-history-list">
                {summary.creditEvents.slice(0, 8).map((e, i) => (
                  <li key={`${e.at}-${i}`} className="usg-history-row">
                    <span className="usg-history-label">
                      {creditEventLabel(e.type)}
                    </span>
                    <span className="usg-history-date">{historyDate(e.at)}</span>
                    <span
                      className={`usg-history-amount${e.credits < 0 ? " is-debit" : ""}`}
                    >
                      {e.credits > 0 ? `+${e.credits}` : e.credits}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          ) : null}
        </aside>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                      */
/* ------------------------------------------------------------------ */

/** Human label for a credit-ledger entry type. */
function creditEventLabel(type: string): string {
  switch (type) {
    case "top_up_purchase":
      return "Top-up";
    case "free_monthly_grant":
    case "manual_monthly_grant":
    case "subscription_monthly_grant":
      return "Monthly credits";
    case "job_charge":
      return "Audit run";
    case "job_refund":
      return "Refund";
    default:
      return "Adjustment";
  }
}

function historyDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function Sparkline({ points }: { points: number[] }) {
  const max = Math.max(1, ...points);
  const width = 600;
  const height = 56;
  const step = points.length > 1 ? width / (points.length - 1) : width;

  // 8px of internal headroom above the tallest point so the spike has
  // some air inside the SVG itself (in addition to the container's
  // padding-top). Previous formula only left ~4px and made the peak
  // visually crowd the container's top edge.
  const path = points.map((v, i) => {
    const x = i * step;
    const y = height - (v / max) * (height - 12) - 4;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const areaPath = `${path} L${width},${height} L0,${height} Z`;

  return (
    <svg
      className="usg-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Credits used per day over the last 30 days"
    >
      <defs>
        <linearGradient id="usg-spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--primary-strong)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="var(--primary-strong)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#usg-spark-fill)" />
      <path d={path} fill="none" stroke="var(--primary-strong)" strokeWidth="1.5" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                            */
/* ------------------------------------------------------------------ */

function UsageSkeleton() {
  return (
    <main className="usg-page" aria-busy="true">
      <header className="usg-page-header">
        <div>
          <span className="sk" style={{ width: 90, height: 28, borderRadius: 6, display: "block" }} />
          <span className="sk" style={{ width: 200, height: 14, borderRadius: 6, marginTop: 8, display: "block" }} />
        </div>
      </header>
      <section className="usg-stats">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="usg-stat">
            <span className="sk" style={{ width: 64, height: 11, borderRadius: 4 }} />
            <span className="sk" style={{ width: 56, height: 24, borderRadius: 4, marginTop: 8 }} />
            <span className="sk" style={{ width: 80, height: 11, borderRadius: 4, marginTop: 8 }} />
          </div>
        ))}
      </section>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function summarize(jobs: JobSummary[]) {
  return jobs.reduce(
    (s, j) => ({
      credits: s.credits + estimateBaseCredits(j),
      pages: s.pages + Math.max(0, j.pageCount || 0),
      runs: s.runs + 1,
    }),
    { credits: 0, pages: 0, runs: 0 },
  );
}

/**
 * estimateBaseCredits — derive an approximate credit cost for a job
 * from its summary shape (pageCount × viewports). This is a fallback
 * only: when the ledger summary endpoint returns data, the UI uses
 * `summary.monthly.credits` (the authoritative number) instead of
 * summing this estimate.
 *
 * Known undercount: jobs that used AI guidance pay a +5 surcharge,
 * which isn't reflected here because JobSummary doesn't currently
 * carry an `aiUsed` flag. The error is small (per-AI-audit, not
 * per-page) and only visible when the ledger summary is unreachable
 * — but if you're chasing this: expose `aiUsed` on JobSummary
 * backend-side and add `+ (job.aiUsed ? 5 : 0)` here.
 */
function estimateBaseCredits(job: JobSummary): number {
  return Math.max(0, job.pageCount || 0) * Math.max(1, job.viewports?.length || 1);
}

function buildTrend(jobs: JobSummary[], days: number): number[] {
  const buckets = new Array(days).fill(0) as number[];
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const start = now - (days - 1) * dayMs;
  for (const j of jobs) {
    if (j.status !== "done") continue;
    if (j.createdAt < start - dayMs) continue;
    const idx = Math.floor((j.createdAt - start) / dayMs);
    if (idx >= 0 && idx < days) buckets[idx] += estimateBaseCredits(j);
  }
  return buckets;
}

function isThisMonth(ts: number): boolean {
  const d = new Date(ts);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function isLastMonth(ts: number): boolean {
  const d = new Date(ts);
  const now = new Date();
  const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return d.getFullYear() === lm.getFullYear() && d.getMonth() === lm.getMonth();
}

function daysToRenewal(): number {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return Math.max(0, Math.ceil((next.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
}

function projectMonthlyTotal(usedSoFar: number, daysLeft: number): number | null {
  const now = new Date();
  const dayOfMonth = now.getDate();
  if (dayOfMonth < 2) return null;
  const dailyRate = usedSoFar / dayOfMonth;
  return Math.round(usedSoFar + dailyRate * daysLeft);
}

function formatRenewalDate(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1)
    .toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatShortDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function hostnameFor(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url || "Untitled"; }
}

function viewportLabel(v: Viewport): string {
  if (v === "desktop") return "Desktop";
  if (v === "mobile") return "Mobile";
  return v as string;
}

function statusLabel(s: string): string {
  if (s === "done") return "Done";
  if (s === "running") return "Running";
  if (s === "error") return "Error";
  return "Queued";
}

function relativeTime(when: number): string {
  const diff = Date.now() - when;
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return mo < 12 ? `${mo}mo ago` : `${Math.round(mo / 12)}y ago`;
}
